// Vues MESSAGES + TERMINAL partagées du plugin cli.
// Composants paramétrés réutilisables par tous les panels par CLI.
import {
  Show,
  For,
  createSignal,
  createMemo,
  createEffect,
  onMount,
  onCleanup,
  type Component,
  type JSX,
} from "solid-js";
import { useToolClient } from "@/shared/tool-client";
import { useWs } from "@/shared/ws";
import { Collapsible } from "@/shared/collapsible";
import { fmtTime, prettyJson, summarizeToolInput } from "./format";
import { aggregate } from "./aggregate";
import type { CliStream } from "./stream";
import type { CliSession, CliReplayFrame, MsgBlock } from "./types";

// ── ToolBlock (bloc outil repliable dans la vue Messages) ─────────────────────

interface ToolBlockProps {
  block: MsgBlock & { kind: "tool" };
  needsApproval: boolean;
  onApprove: () => void;
}

export const ToolBlock: Component<ToolBlockProps> = (props) => (
  <Collapsible
    variant="tool"
    class="cli-tool-block"
    header={
      <span class="cli-tool-head">
        <span class="cli-tool-name">🔧 {props.block.toolName}</span>
        <span class="cli-tool-summary">{summarizeToolInput(props.block.toolInput)}</span>
        <Show when={props.needsApproval}>
          <button
            class="btn btn--xs btn--primary cli-tool-approve"
            onClick={(e) => {
              e.stopPropagation();
              props.onApprove();
            }}
            title="Approuver l'exécution de cet outil (injecte userConfirmedAt)"
          >
            Approuver
          </button>
        </Show>
      </span>
    }
  >
    <pre class="cli-json">{prettyJson(props.block.toolInput)}</pre>
    <Show when={props.block.toolResult !== null && props.block.toolResult !== undefined}>
      <Collapsible variant="result" class="cli-tool-result" header={<span>✓ résultat</span>}>
        <pre class="cli-json cli-tool-result-pre">{prettyJson(props.block.toolResult)}</pre>
      </Collapsible>
    </Show>
  </Collapsible>
);

// ── MessagesView (mode structuré) ─────────────────────────────────────────────

interface MessagesViewProps {
  session: CliSession;
  stream: CliStream;
  client: ReturnType<typeof useToolClient>;
}

export const MessagesView: Component<MessagesViewProps> = (props) => {
  const [draft, setDraft] = createSignal("");
  const [sending, setSending] = createSignal(false);
  const [err, setErr] = createSignal("");
  let historyRef: HTMLDivElement | undefined;
  let inputRef: HTMLTextAreaElement | undefined;

  const blocks = createMemo(() => aggregate(props.stream.events()));

  createEffect(() => {
    // auto-scroll au bas à chaque nouveau bloc
    blocks();
    requestAnimationFrame(() => historyRef?.scrollTo(0, historyRef.scrollHeight));
  });

  async function send() {
    const text = draft().trim();
    if (!text || sending()) return;
    setSending(true);
    setErr("");
    try {
      await props.client.call("cli_send_message", { sessionId: props.session.sessionId, text });
      setDraft("");
      inputRef?.focus();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  }

  async function steer() {
    const text = draft().trim();
    if (!text) return;
    setErr("");
    try {
      await props.client.call("cli_steer", { sessionId: props.session.sessionId, text });
      setDraft("");
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  async function approveTool(block: MsgBlock & { kind: "tool" }) {
    // Réémet l'approbation : injecte userConfirmedAt. Le relais reste source de vérité.
    try {
      await props.client.call(
        "cli_send_message",
        { sessionId: props.session.sessionId, approveToolUseId: block.nativeId },
        { userConfirmedAt: new Date().toISOString() },
      );
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div class="cli-messages">
      <div class="cli-messages-history" ref={historyRef}>
        <Show
          when={blocks().length > 0}
          fallback={<div class="panel-empty">Aucun message. Envoyez une instruction pour démarrer.</div>}
        >
          <For each={blocks()}>
            {(b) => (
              <Show when={b.kind === "tool"} fallback={renderSimpleBlock(b)}>
                <ToolBlock
                  block={b as MsgBlock & { kind: "tool" }}
                  needsApproval={(b as MsgBlock & { kind: "tool" }).needsApproval}
                  onApprove={() => approveTool(b as MsgBlock & { kind: "tool" })}
                />
              </Show>
            )}
          </For>
        </Show>
      </div>

      <Show when={err()}>
        <div class="panel-error cli-messages-err">{err()}</div>
      </Show>

      <div class="cli-messages-input-row">
        <textarea
          ref={inputRef}
          class="cli-messages-input"
          value={draft()}
          rows={2}
          placeholder="Envoyer un message (Entrée = envoyer, Maj+Entrée = nouvelle ligne)…"
          disabled={props.session.status === "exited"}
          onInput={(e) => setDraft(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
        />
        <div class="cli-messages-buttons">
          <button class="btn btn--sm" onClick={() => void send()} disabled={sending() || !draft().trim()}>
            {sending() ? "Envoi…" : "Envoyer"}
          </button>
          <button
            class="btn btn--sm btn--ghost"
            onClick={() => void steer()}
            disabled={!draft().trim()}
            title="Réorienter (steer) — Codex mid-flight"
          >
            Steer
          </button>
        </div>
      </div>
    </div>
  );
};

export function renderSimpleBlock(b: MsgBlock): JSX.Element {
  if (b.kind === "tool") return null; // géré séparément
  if (b.kind === "thinking") {
    return (
      <Collapsible variant="thinking" class="cli-thinking" header={<span>💭 Réflexion</span>}>
        <pre class="cli-thinking-body">{b.content}</pre>
      </Collapsible>
    );
  }
  if (b.kind === "status") {
    return <div class="cli-status-line">ⓘ {b.content}</div>;
  }
  if (b.kind === "error") {
    return <div class="cli-error-line">⚠ {b.content}</div>;
  }
  // user / assistant → bulles
  return (
    <div class="cli-bubble" data-role={b.kind}>
      <div class="cli-bubble-meta">
        <span class="cli-bubble-author">{b.kind === "user" ? "Opérateur" : "Assistant"}</span>
        <Show when={fmtTime(b.at)}>
          <span class="cli-bubble-time">{fmtTime(b.at)}</span>
        </Show>
      </div>
      <div class="cli-bubble-body">{b.content}</div>
    </div>
  );
}

// ── TerminalView (mode PTY, xterm.js dynamique) ───────────────────────────────

// Type minimal pour éviter une dépendance de types top-level sur @xterm/xterm.
interface XTermLike {
  open(el: HTMLElement): void;
  write(data: string): void;
  onData(cb: (data: string) => void): { dispose: () => void };
  dispose(): void;
  cols: number;
  rows: number;
}

interface TerminalViewProps {
  session: CliSession;
  client: ReturnType<typeof useToolClient>;
  stream: CliStream;
}

export const TerminalView: Component<TerminalViewProps> = (props) => {
  const ws = useWs();
  let containerRef: HTMLDivElement | undefined;
  let term: XTermLike | null = null;
  let disposed = false;
  let writtenFrames = 0;

  // Décode et écrit un frame PTY (base64 → octets → string). convertEol gère \n.
  function writeB64(b64: string) {
    if (!term) return;
    try {
      term.write(atob(b64));
    } catch {
      // ignore frame illisible
    }
  }

  // Effet de montage : clone EXACT du pattern powershell.tsx (replay createEffect
  // ~579-634) adapté au type WS `cli:session:replay`.
  createEffect(() => {
    const sessionId = props.session.sessionId;
    if (!sessionId) return;

    let cancelled = false;

    void (async () => {
      // Import dynamique lazy de xterm (jamais top-level — CONTRACTS §11).
      const [{ Terminal }] = await Promise.all([import("@xterm/xterm"), import("@xterm/xterm/css/xterm.css")]);
      if (cancelled || disposed || !containerRef) return;
      const t = new Terminal({ convertEol: true, scrollback: 5000 }) as unknown as XTermLike;
      t.open(containerRef);
      term = t;

      // input clavier brut → cli_pty_send (interop xterm impératif, pas un scope JSX)
      // eslint-disable-next-line solid/reactivity
      t.onData((data) => {
        void props.client.call("cli_pty_send", { sessionId, data }).catch(() => undefined);
      });

      // resize initial → cli_pty_resize
      void props.client.call("cli_pty_resize", { sessionId, cols: t.cols, rows: t.rows }).catch(() => undefined);

      // rejoue les frames live déjà bufferisées (avant que le term soit prêt)
      const buffered = props.stream.frames();
      for (const f of buffered) writeB64(f.dataB64);
      writtenFrames = buffered.length;
    })();

    // Replay scrollback via type WS dédié (clone du ps:session:replay)
    const replayRequestId = `cli_replay_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const offReplay = ws.on(
      (m) => m.type === "cli:session:replay:result" && (m as { requestId?: string }).requestId === replayRequestId,
      (m: unknown) => {
        const msg = m as { ok?: boolean; result?: { frames?: CliReplayFrame[] }; error?: string };
        if (!msg.ok) return;
        const frames = msg.result?.frames || [];
        for (const fr of frames) {
          if (fr.kind === "raw" && term) {
            // frame raw = base64 dans le JSON stocké { sessionId, stream, dataB64 } OU directement raw
            try {
              const parsed = JSON.parse(fr.json) as { dataB64?: string; raw?: string };
              const b64 = parsed.dataB64 || parsed.raw;
              if (b64) writeB64(b64);
            } catch {
              // ignore
            }
          }
        }
      },
    );
    onCleanup(offReplay);

    ws.send({
      type: "cli:session:replay",
      requestId: replayRequestId,
      sessionId,
      sinceSeq: 0,
    } as unknown as Parameters<typeof ws.send>[0]);

    onCleanup(() => {
      cancelled = true;
      if (term) {
        try {
          term.dispose();
        } catch {
          // ignore
        }
        term = null;
      }
    });
  });

  // Écrit les nouveaux frames live au fil de l'eau (au-delà de ceux déjà rejoués).
  createEffect(() => {
    const all = props.stream.frames();
    if (!term) return;
    for (let i = writtenFrames; i < all.length; i++) writeB64(all[i].dataB64);
    writtenFrames = all.length;
  });

  function onResize() {
    if (!term) return;
    void props.client
      .call("cli_pty_resize", { sessionId: props.session.sessionId, cols: term.cols, rows: term.rows })
      .catch(() => undefined);
  }

  onMount(() => {
    window.addEventListener("resize", onResize);
  });
  onCleanup(() => {
    disposed = true;
    window.removeEventListener("resize", onResize);
  });

  return (
    <div class="cli-terminal">
      <div class="cli-terminal-host" ref={containerRef} />
    </div>
  );
};
